import { AfterViewInit, Component, forwardRef, Inject, Injector, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, FormControlDirective, FormControlName, FormGroupDirective, FormsModule, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MaterialModule } from 'src/app/material.module';
import moment from 'moment';
import { CommonModule, DatePipe } from '@angular/common';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { isValidDate } from 'src/app/shared/utils';
import { IDatePickerConfig } from 'src/app/shared/interfaces/IDate-picker';
import { DATE_FORMATS, DATE_PICKER_LOCALE } from 'src/app/shared/constants';

@Component({
  selector: 'app-form-date-picker',
  standalone: true,
  imports: [MaterialModule, MatDatepickerModule, MatNativeDateModule, ReactiveFormsModule, FormsModule, CommonModule],
  templateUrl: './form-date-picker.component.html',
  styleUrl: './form-date-picker.component.scss',
  providers: [
    {
      // to connect FormControl and custom component
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormDatePickerComponent),
      multi: true
    },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: DATE_PICKER_LOCALE },
    DatePipe
  ]
})
export class FormDatePickerComponent implements ControlValueAccessor, OnChanges, AfterViewInit, OnInit {
  @Input() datePickerConfig: IDatePickerConfig;
  public selectedDate: any;
  formControl: FormControl = new FormControl('');
  onChange: (provinceData: any) => void;
  onTouched: () => void;
  isDisabled: boolean;
  placeholderText: string = DATE_FORMATS.parse.dateInput;
  isInputDisabled = false;
  constructor(@Inject(Injector) private injector: Injector) {}

  get isControlInvalid(): boolean {
    return this?.formControl?.invalid && this?.formControl?.touched;
  }

  ngOnInit() {
    this.initializeFormControl();
    if (!this.datePickerConfig.placeHolder) {
      this.placeholderText = DATE_FORMATS.parse.dateInput;
    } else {
      this.placeholderText = this.datePickerConfig.placeHolder;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['datePickerConfig']) {
      if (this.datePickerConfig?.minDate instanceof Date) {
        this.datePickerConfig.minDate = this.datePickerConfig.minDate?.toISOString();
      }
      if (this.datePickerConfig.maxDate instanceof Date) {
        this.datePickerConfig.maxDate = this.datePickerConfig.maxDate?.toISOString();
      }
    }
    this.isInputDisabled = !!(this.datePickerConfig?.minDate || this.datePickerConfig?.maxDate);
  }

  ngAfterViewInit(): void {
    if (this.datePickerConfig.startDay instanceof Date) {
      this.writeValue(this.datePickerConfig.startDay);
    } else if (this.datePickerConfig.startDay?.toLowerCase() == 'now') {
      this.writeValue(new Date());
    }
  }

  writeValue(obj: any): void {
    this.selectedDate = obj;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  handleDateChange() {
    let dateString: any = moment(this.selectedDate, 'DD.MM.YYYY').format('YYYY-MM-DD');
    if (dateString === 'Invalid date') {
      dateString = null;
    }
    this.writeValue(dateString);
    this.onChange(dateString);
    this.datePickerConfig.dateChanged && this.datePickerConfig.dateChanged(dateString);
  }

  handleInputChange(event: any) {
    let dateString = null;
    if (isValidDate(event.target.value)) {
      dateString = moment(this.selectedDate, 'DD.MM.YYYY').format('YYYY-MM-DD');
      if (dateString === 'Invalid date') {
        dateString = null;
      }
      this.onChange(dateString);
    } else {
      this.onChange(null);
    }
  }

  initializeFormControl() {
    try {
      const formControl = this.injector.get(NgControl);
      switch (formControl.constructor) {
        case FormControlName:
          this.formControl = this.injector.get(FormGroupDirective).getControl(formControl as FormControlName);
          break;
        default:
          this.formControl = (formControl as FormControlDirective).form as FormControl;
          break;
      }
    } catch (err) {
      console.error(err);
      this.formControl = new FormControl();
    }
  }
}
